import { useState, useEffect } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, RefreshCw, Eye, Edit2, Trash2, Image as ImageIcon } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BulkActionDialogs, type BulkActionResult } from "@/components/BulkActionDialogs";
import type { FirestoreGame } from "@/../../server/db-games-firestore";
import { useGameIconUrl } from "@/lib/firebaseStorage";

// Use FirestoreGame type directly, ensuring id is always a string
type GameData = Omit<FirestoreGame, 'id'> & { id: string };

/**
 * Component to display game icon from Firebase Storage
 * Falls back to game.image (Google Play Store) if Firebase URL fails
 */
function GameIconCell({ game }: { game: GameData }) {
  const { url: firebaseUrl, elementRef } = useGameIconUrl(game.gameKey, true);
  const [showFallback, setShowFallback] = useState(false);

  const imageUrl = firebaseUrl || game.image || game.imageUrl;

  return (
    <div 
      ref={elementRef}
      className="relative w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden"
    >
      {imageUrl && !showFallback ? (
        <img
          src={imageUrl}
          alt={game.name}
          className="w-10 h-10 rounded object-cover"
          loading="lazy"
          decoding="async"
          onError={() => setShowFallback(true)}
        />
      ) : imageUrl && showFallback ? (
        <img
          src={game.image || game.imageUrl || ""}
          alt={game.name}
          className="w-10 h-10 rounded object-cover"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : (
        <ImageIcon className="w-5 h-5 text-gray-400" />
      )}
    </div>
  );
}

export default function AdminGameList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGame, setSelectedGame] = useState<GameData | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set());
  const [isImporting, setIsImporting] = useState(false);
  const [showBulkStatusDialog, setShowBulkStatusDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<"active" | "inactive">("active");
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkTier, setBulkTier] = useState<"S" | "A" | "B" | "C" | "">("" as any);
  const [bulkImageUrl, setBulkImageUrl] = useState("");
  const [showBulkCategoryDialog, setShowBulkCategoryDialog] = useState(false);
  const [showBulkTierDialog, setShowBulkTierDialog] = useState(false);
  const [showBulkImageDialog, setShowBulkImageDialog] = useState(false);
  const [bulkActionResult, setBulkActionResult] = useState<BulkActionResult | null>(null);
  const [, setLocation] = useLocation();

  // Bulk mutations using Firestore
  const bulkUpdateStatusMutation = trpc.admin.gamesFirestore.bulkUpdateGameStatus.useMutation();
  const bulkDeleteMutation = trpc.admin.gamesFirestore.bulkDeleteGames.useMutation();
  const bulkUpdateCategoryMutation = trpc.admin.gamesFirestore.bulkUpdateGameCategory.useMutation();
  const bulkUpdateTierMutation = trpc.admin.gamesFirestore.bulkUpdateGameTier.useMutation();
  const bulkReplaceImagesMutation = trpc.admin.gamesFirestore.bulkReplaceGameImages.useMutation();

  // Fetch games from Firestore via admin API
  const { data: gamesData, isLoading: isLoadingGames, refetch: refetchGames } = trpc.admin.gamesFirestore.listGames.useQuery({
    limit: 100,
    offset: 0,
  });

  // Ensure games have id field
  const games: GameData[] = (gamesData?.games || []).map((game: any) => ({
    ...game,
    id: game.id || '',
  }));
  const total = gamesData?.total || 0;

  const filteredGames = games.filter((game: GameData) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      game.name?.toLowerCase().includes(searchLower) ||
      game.gameId?.toLowerCase().includes(searchLower) ||
      game.gameKey?.toLowerCase().includes(searchLower)
    );
  });

  const toggleGameSelection = (gameId: string) => {
    const newSelected = new Set(selectedGameIds);
    if (newSelected.has(gameId)) {
      newSelected.delete(gameId);
    } else {
      newSelected.add(gameId);
    }
    setSelectedGameIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedGameIds.size === filteredGames.length) {
      setSelectedGameIds(new Set());
    } else {
      setSelectedGameIds(new Set(filteredGames.map((g: GameData) => g.id)));
    }
  };

  const handleBulkStatusUpdate = async () => {
    try {
      await bulkUpdateStatusMutation.mutateAsync({
        gameIds: Array.from(selectedGameIds),
        status: bulkStatus,
      });
      alert(`Successfully updated ${selectedGameIds.size} games to ${bulkStatus}`);
      setShowBulkStatusDialog(false);
      setSelectedGameIds(new Set());
      await refetchGames();
    } catch (error) {
      alert(`Error updating games: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteMutation.mutateAsync({
        gameIds: Array.from(selectedGameIds),
      });
      alert(`Successfully deleted ${selectedGameIds.size} games`);
      setShowBulkDeleteDialog(false);
      setSelectedGameIds(new Set());
      await refetchGames();
    } catch (error) {
      alert(`Error deleting games: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleBulkCategoryUpdate = async () => {
    if (!bulkCategory.trim()) {
      alert("Please enter a category");
      return;
    }
    try {
      const result = await bulkUpdateCategoryMutation.mutateAsync({
        gameIds: Array.from(selectedGameIds),
        category: bulkCategory,
      });
      setBulkActionResult(result);
      setShowBulkCategoryDialog(false);
      setBulkCategory("");
      setSelectedGameIds(new Set());
      refetchGames();
    } catch (error) {
      alert(`Error updating category: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleBulkTierUpdate = async () => {
    if (!bulkTier) {
      alert("Please select a tier");
      return;
    }
    try {
      const result = await bulkUpdateTierMutation.mutateAsync({
        gameIds: Array.from(selectedGameIds),
        tier: bulkTier as "S" | "A" | "B" | "C",
      });
      setBulkActionResult(result);
      setShowBulkTierDialog(false);
      setBulkTier("" as any);
      setSelectedGameIds(new Set());
      refetchGames();
    } catch (error) {
      alert(`Error updating tier: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleBulkImageReplace = async () => {
    if (!bulkImageUrl.trim()) {
      alert("Please enter an image URL");
      return;
    }
    try {
      const result = await bulkReplaceImagesMutation.mutateAsync({
        gameIds: Array.from(selectedGameIds),
        imageUrl: bulkImageUrl,
      });
      setBulkActionResult(result);
      setShowBulkImageDialog(false);
      setBulkImageUrl("");
      setSelectedGameIds(new Set());
      refetchGames();
    } catch (error) {
      alert(`Error replacing images: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const getTierBadge = (tier: string | undefined) => {
    const tierColors: Record<string, string> = {
      S: "bg-red-600",
      A: "bg-orange-600",
      B: "bg-yellow-600",
      C: "bg-blue-600",
    };
    return <Badge className={tierColors[tier || 'C'] || "bg-gray-600"}>{tier || 'C'}</Badge>;
  };

  const getStatusBadge = (status: string | undefined) => {
    return status === "active" ? (
      <Badge className="bg-green-600">Active</Badge>
    ) : (
      <Badge className="bg-red-600">Inactive</Badge>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-black">Game Management</h1>
          <p className="text-black">Manage games, packages, and servers</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border-2 border-black">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-black">Total Games</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-black">{total}</div>
            </CardContent>
          </Card>
          <Card className="bg-white border-2 border-black">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-black">Active Games</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {games.filter((g: GameData) => g.status === "active").length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-2 border-black">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-black">Inactive Games</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {games.filter((g: GameData) => g.status === "inactive").length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-2 border-black">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-black">Tier S Games</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {games.filter((g: GameData) => g.tier === "S").length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div>
          <Input
            placeholder="Search games by name, ID, or key..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md border-2 border-black text-black"
          />
        </div>

        {/* Bulk Action Toolbar */}
        {selectedGameIds.size > 0 && (
          <Card className="bg-white border-2 border-black">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">
                    {selectedGameIds.size} game(s) selected
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBulkStatusDialog(true)}
                    disabled={bulkUpdateStatusMutation.isPending}
                  >
                    {bulkUpdateStatusMutation.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating...</>
                    ) : (
                      "Update Status"
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBulkCategoryDialog(true)}
                    disabled={bulkUpdateCategoryMutation.isPending}
                  >
                    {bulkUpdateCategoryMutation.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating...</>
                    ) : (
                      "Update Category"
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBulkTierDialog(true)}
                    disabled={bulkUpdateTierMutation.isPending}
                  >
                    {bulkUpdateTierMutation.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating...</>
                    ) : (
                      "Update Tier"
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBulkImageDialog(true)}
                    disabled={bulkReplaceImagesMutation.isPending}
                  >
                    {bulkReplaceImagesMutation.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Replacing...</>
                    ) : (
                      "Replace Images"
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowBulkDeleteDialog(true)}
                    disabled={bulkDeleteMutation.isPending}
                  >
                    {bulkDeleteMutation.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
                    ) : (
                      "Delete"
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Games Table */}
        <Card className="bg-white border-2 border-black">
          <CardHeader>
            <CardTitle className="text-black">Games List</CardTitle>
            <CardDescription className="text-black">
              {filteredGames.length} of {total} games
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingGames ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-black">
                    <TableRow>
                      <TableHead className="w-12 bg-black">
                        <Checkbox
                          checked={selectedGameIds.size === filteredGames.length && filteredGames.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="text-white bg-black">Game Name</TableHead>
                      <TableHead className="text-white bg-black">Game ID</TableHead>
                      <TableHead className="text-white bg-black">Game Key</TableHead>
                      <TableHead className="text-white bg-black">Category</TableHead>
                      <TableHead className="text-white bg-black">Tier</TableHead>
                      <TableHead className="text-white bg-black">Status</TableHead>
                      <TableHead className="text-white bg-black">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredGames.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-black">
                          No games found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredGames.map((game: GameData) => (
                        <TableRow key={game.id} className="hover:bg-gray-100">
                          <TableCell className="w-12">
                            <Checkbox
                              checked={selectedGameIds.has(game.id)}
                              onCheckedChange={() => toggleGameSelection(game.id)}
                            />
                          </TableCell>
                          <TableCell className="font-medium text-black">
                            <div className="flex items-center gap-2">
                              <GameIconCell game={game} />
                              <span className="truncate">{game.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-black">{game.gameId}</TableCell>
                          <TableCell className="text-sm text-black">{game.gameKey}</TableCell>
                          <TableCell className="text-black">{game.category || "-"}</TableCell>
                          <TableCell>{getTierBadge(game.tier)}</TableCell>
                          <TableCell>{getStatusBadge(game.status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setLocation(`/admin/games/${game.gameKey || game.id}`)}
                    title="Edit game"
                    className="text-black hover:text-black hover:bg-gray-100"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Game Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedGame?.name}</DialogTitle>
              <DialogDescription>Game details</DialogDescription>
            </DialogHeader>
            {selectedGame && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Game ID</label>
                    <p className="text-gray-600">{selectedGame.gameId}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Game Key</label>
                    <p className="text-gray-600">{selectedGame.gameKey}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Category</label>
                    <p className="text-gray-600">{selectedGame.category || "-"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Tier</label>
                    <p>{getTierBadge(selectedGame.tier)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Status</label>
                    <p>{getStatusBadge(selectedGame.status)}</p>
                  </div>
                </div>

                {selectedGame.description && (
                  <div>
                    <label className="text-sm font-medium">Description</label>
                    <p className="text-gray-600">{selectedGame.description}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk Status Update Dialog */}
        <Dialog open={showBulkStatusDialog} onOpenChange={setShowBulkStatusDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Game Status</DialogTitle>
              <DialogDescription>
                Update status for {selectedGameIds.size} selected game(s)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">New Status</label>
                <Select value={bulkStatus} onValueChange={(value: any) => setBulkStatus(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowBulkStatusDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkStatusUpdate}
                  disabled={bulkUpdateStatusMutation.isPending}
                >
                  {bulkUpdateStatusMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating...</>
                  ) : (
                    "Update"
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Dialog */}
        <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Games</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete {selectedGameIds.size} selected game(s)? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={bulkDeleteMutation.isPending}
              >
                {bulkDeleteMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
                ) : (
                  "Delete"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Action Dialogs Component */}
        <BulkActionDialogs
          showCategoryDialog={showBulkCategoryDialog}
          onCategoryDialogOpenChange={setShowBulkCategoryDialog}
          categoryValue={bulkCategory}
          onCategoryChange={setBulkCategory}
          onCategorySubmit={handleBulkCategoryUpdate}
          isCategoryLoading={bulkUpdateCategoryMutation.isPending}
          showTierDialog={showBulkTierDialog}
          onTierDialogOpenChange={setShowBulkTierDialog}
          tierValue={bulkTier}
          onTierChange={(value) => setBulkTier(value as any)}
          onTierSubmit={handleBulkTierUpdate}
          isTierLoading={bulkUpdateTierMutation.isPending}
          showImageDialog={showBulkImageDialog}
          onImageDialogOpenChange={setShowBulkImageDialog}
          imageUrl={bulkImageUrl}
          onImageUrlChange={setBulkImageUrl}
          onImageSubmit={handleBulkImageReplace}
          isImageLoading={bulkReplaceImagesMutation.isPending}
          result={bulkActionResult}
          onResultClose={() => setBulkActionResult(null)}
          selectedCount={selectedGameIds.size}
        />
      </div>
    </AdminLayout>
  );
}
